import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { useSearchThemes } from "../../../hooks/useSearchThemes";
import { useGetSubThemes } from "../../../hooks/useGetSubThemes";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { useAuth } from "../../../context/AuthContext";
import { toastAlert } from "../../../utils/AlertContext";

const TEMA_QUERY_MIN_LENGTH = 3;
const TEMA_QUERY_DEBOUNCE_MS = 400;

const getThemeId = (tema) => tema?.TEXCLAVE || tema?.id;
const getThemeName = (tema) => tema?.TEXNOMBRE || tema?.name || "";

const getThemeOptionLabel = (tema) =>
  `${getThemeName(tema)} (ID: ${getThemeId(tema)})`;

const getSubThemeId = (subtema) => subtema?.XID || subtema?.id;
const getSubThemeName = (subtema) => subtema?.DESCRIPCION || subtema?.name || "";

const renameSubtema = (subtemas, subtemaId, nuevoNombre) =>
  subtemas.map((subtema) =>
    subtema.id === subtemaId ? { ...subtema, nombrePersonalizado: nuevoNombre } : subtema
  );

const SubtemasFields = ({ subtemas, onRename }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
      gap: 1,
      mt: 1,
    }}
  >
    {subtemas.map((subtema) => (
      <TextField
        key={subtema.id}
        fullWidth
        size="small"
        label={`${subtema.nombre || "Sin nombre"} (ID: ${subtema.id})`}
        value={subtema.nombrePersonalizado ?? ""}
        onChange={(e) => onRename(subtema.id, e.target.value)}
        error={!subtema.nombrePersonalizado?.trim()}
        helperText={!subtema.nombrePersonalizado?.trim() ? "El nombre es requerido" : ""}
      />
    ))}
  </Box>
);

const SortableTema = ({ item, index, onDelete, onRename, onRenameSubtema }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1.5,
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        background: isDragging ? "#e3f0fa" : "#ffffff",
        boxShadow: isDragging ? "0 4px 12px rgba(0,0,0,0.12)" : "none",
        position: "relative",
        zIndex: isDragging ? 1 : "auto",
      }}
    >
      <Tooltip title="Arrastrar para reordenar">
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            cursor: "grab",
            color: "#9e9e9e",
            "&:hover": { color: "#1976d2" },
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorIcon />
        </Box>
      </Tooltip>

      <Typography fontWeight={600} sx={{ minWidth: 28 }}>
        {index + 1}.
      </Typography>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          Tema: {item.nombre || "Sin nombre"} (ID: {item.id})
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Nombre personalizado"
          value={item.nombrePersonalizado ?? ""}
          onChange={(e) => onRename(item.id, e.target.value)}
          error={!item.nombrePersonalizado?.trim()}
          helperText={!item.nombrePersonalizado?.trim() ? "El nombre es requerido" : ""}
          sx={{ mt: 1 }}
        />
        {item.subtemas?.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
              Subtemas ({item.subtemas.length})
            </Typography>
            <SubtemasFields
              subtemas={item.subtemas}
              onRename={(subtemaId, nuevoNombre) =>
                onRenameSubtema(item.id, subtemaId, nuevoNombre)
              }
            />
          </>
        )}
      </Box>

      <Tooltip title="Eliminar tema">
        <IconButton onClick={() => onDelete(item.id)}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export const Temas = ({ value, onChange }) => {
  const { token } = useAuth();
  const { themes, loadingThemes, errorThemes, searchThemes } = useSearchThemes(token);
  const { subThemes, loadingSubThemes, errorSubThemes, fetchSubThemes } =
    useGetSubThemes(token);

  const [temaQuery, setTemaQuery] = useState("");
  const [temaSeleccionado, setTemaSeleccionado] = useState(null);
  const [nombrePersonalizado, setNombrePersonalizado] = useState("");
  const [subtemas, setSubtemas] = useState([]);

  const items = Array.isArray(value)
    ? value.filter((item) => item?.id !== undefined && item?.id !== null)
    : [];

  const debouncedTemaQuery = useDebouncedValue(temaQuery, TEMA_QUERY_DEBOUNCE_MS);

  useEffect(() => {
    const trimmedQuery = (debouncedTemaQuery || "").trim();
    if (trimmedQuery.length < TEMA_QUERY_MIN_LENGTH) return;
    // El texto es la etiqueta del tema ya elegido: no se vuelve a buscar
    if (temaSeleccionado && trimmedQuery === getThemeOptionLabel(temaSeleccionado)) return;

    searchThemes(trimmedQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTemaQuery]);

  // Al llegar los subtemas del tema elegido se propone su nombre original;
  // el usuario puede cambiar el de cada uno
  useEffect(() => {
    if (!temaSeleccionado) return;
    setSubtemas(
      subThemes.map((subtema) => ({
        id: Number(getSubThemeId(subtema)),
        nombre: getSubThemeName(subtema),
        nombrePersonalizado: getSubThemeName(subtema),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subThemes]);

  const subtemasIncompletos = subtemas.some(
    (subtema) => !subtema.nombrePersonalizado.trim()
  );

  const isAdded = (tema) =>
    items.some((item) => Number(item.id) === Number(getThemeId(tema)));

  const handleAdd = () => {
    if (!temaSeleccionado) {
      toastAlert("Selecciona un tema antes de agregarlo.", "warning");
      return;
    }

    if (isAdded(temaSeleccionado)) {
      toastAlert("Este tema ya está en la lista.", "warning");
      return;
    }

    if (!nombrePersonalizado.trim()) {
      toastAlert("Escribe el nombre personalizado del tema.", "warning");
      return;
    }

    if (loadingSubThemes) {
      toastAlert("Espera a que carguen los subtemas.", "warning");
      return;
    }

    if (subtemasIncompletos) {
      toastAlert("Escribe el nombre personalizado de todos los subtemas.", "warning");
      return;
    }

    onChange([
      ...items,
      {
        id: Number(getThemeId(temaSeleccionado)),
        nombre: getThemeName(temaSeleccionado),
        nombrePersonalizado: nombrePersonalizado.trim(),
        subtemas: subtemas.map((subtema) => ({
          ...subtema,
          nombrePersonalizado: subtema.nombrePersonalizado.trim(),
        })),
      },
    ]);
    setTemaSeleccionado(null);
    setTemaQuery("");
    setNombrePersonalizado("");
    setSubtemas([]);
  };

  const handleRename = (id, nuevoNombre) => {
    onChange(
      items.map((item) =>
        item.id === id ? { ...item, nombrePersonalizado: nuevoNombre } : item
      )
    );
  };

  const handleRenameSubtema = (id, subtemaId, nuevoNombre) => {
    onChange(
      items.map((item) =>
        item.id === id
          ? { ...item, subtemas: renameSubtema(item.subtemas || [], subtemaId, nuevoNombre) }
          : item
      )
    );
  };

  const handleDelete = (id) => {
    onChange(items.filter((item) => item.id !== id));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    onChange(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <Stack spacing={3} mt={2}>
      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Typography fontWeight={600} mb={1.5}>
          Agregar tema
        </Typography>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
          <Autocomplete
            sx={{ flex: 1, minWidth: 0, width: { xs: "100%", md: "auto" } }}
            options={themes}
            getOptionLabel={(tema) => (tema ? getThemeOptionLabel(tema) : "")}
            getOptionDisabled={isAdded}
            isOptionEqualToValue={(option, val) => getThemeId(option) === getThemeId(val)}
            filterOptions={(options) => options} // el filtrado ya lo hace el backend
            loading={loadingThemes}
            loadingText="Buscando temas..."
            noOptionsText={
              temaQuery.trim().length < TEMA_QUERY_MIN_LENGTH
                ? `Escribe al menos ${TEMA_QUERY_MIN_LENGTH} caracteres para ver coincidencias...`
                : "No se encontraron temas"
            }
            inputValue={temaQuery}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input" || reason === "reset") {
                setTemaQuery(newInputValue);
              } else if (reason === "clear") {
                setTemaQuery("");
                setTemaSeleccionado(null);
                setNombrePersonalizado("");
                setSubtemas([]);
              }
            }}
            value={temaSeleccionado}
            onChange={(event, newValue) => {
              setTemaSeleccionado(newValue || null);
              // Se propone el nombre del tema; el usuario puede cambiarlo
              setNombrePersonalizado(newValue ? getThemeName(newValue) : "");
              setSubtemas([]);
              if (newValue) fetchSubThemes(getThemeId(newValue));
            }}
            renderOption={(props, tema) => (
              <li {...props} key={getThemeId(tema)}>
                {getThemeOptionLabel(tema)}
                {isAdded(tema) ? " — ya agregado" : ""}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar tema (Query) por nombre o ID"
                placeholder='Ejemplo: "política" o "12345"'
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingThemes ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            label="Nombre personalizado"
            value={nombrePersonalizado}
            onChange={(e) => setNombrePersonalizado(e.target.value)}
            disabled={!temaSeleccionado}
            sx={{ flex: 1, minWidth: 0, width: { xs: "100%", md: "auto" } }}
          />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={
              !temaSeleccionado ||
              !nombrePersonalizado.trim() ||
              loadingSubThemes ||
              subtemasIncompletos
            }
            sx={{ minWidth: 140, height: 56, width: { xs: "100%", md: "auto" } }}
          >
            Agregar
          </Button>
        </Stack>

        {temaSeleccionado && (
          <Box mt={2}>
            <Typography variant="body2" fontWeight={600}>
              Subtemas{!loadingSubThemes && ` (${subtemas.length})`}
            </Typography>
            {loadingSubThemes ? (
              <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Cargando subtemas...
                </Typography>
              </Stack>
            ) : subtemas.length === 0 ? (
              <Typography variant="body2" color="text.secondary" mt={1}>
                No hay subtemas para este tema
              </Typography>
            ) : (
              <SubtemasFields
                subtemas={subtemas}
                onRename={(subtemaId, nuevoNombre) =>
                  setSubtemas((prev) => renameSubtema(prev, subtemaId, nuevoNombre))
                }
              />
            )}
          </Box>
        )}

        {(errorThemes || errorSubThemes) && (
          <Typography variant="body2" color="error" mt={1}>
            {errorThemes || errorSubThemes}
          </Typography>
        )}
      </Box>

      <Box>
        <Typography fontWeight={600} mb={1.5}>
          Temas agregados ({items.length})
        </Typography>

        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aún no hay temas. Busca un tema y agrégalo a la lista.
          </Typography>
        ) : (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={items.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1}>
                {items.map((item, index) => (
                  <SortableTema
                    key={item.id}
                    item={item}
                    index={index}
                    onDelete={handleDelete}
                    onRename={handleRename}
                    onRenameSubtema={handleRenameSubtema}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        )}
      </Box>
    </Stack>
  );
};
